/**
 * useGeminiLiveSession Hook
 *
 * React hook for managing real-time voice sessions with Gemini Live API.
 * Handles WebSocket connection, audio capture/playback, and session state.
 *
 * Features:
 * - WebSocket connection to backend voice proxy
 * - AudioWorklet for 16kHz PCM capture
 * - Web Audio API for 24kHz playback
 * - Automatic reconnection
 * - Transcript history
 *
 * @example
 * ```tsx
 * const { state, connect, disconnect, startRecording, stopRecording, transcript } = useGeminiLiveSession();
 *
 * // Connect to voice session
 * await connect({ voice: 'Kore' });
 *
 * // Start recording
 * await startRecording();
 *
 * // Stop recording
 * stopRecording();
 *
 * // Disconnect
 * disconnect();
 * ```
 */

import { useState, useCallback, useRef, useEffect } from 'react';

// =============================================================================
// Types
// =============================================================================

export type VoiceSessionState =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'listening'
  | 'processing'
  | 'speaking'
  | 'error';

export type GeminiVoice =
  | 'Kore'
  | 'Puck'
  | 'Charon'
  | 'Zephyr'
  | 'Enceladus'
  | 'Algieba';

export interface TranscriptEntry {
  role: 'user' | 'assistant';
  text: string;
  timestamp: Date;
}

export interface ToolCallInfo {
  id: string;
  name: string;
  args: Record<string, unknown>;
  result?: unknown;
}

export interface VoiceSessionConfig {
  voice?: GeminiVoice;
  wsUrl?: string;
}

interface ServerMessage {
  type: string;
  sessionId?: string;
  data?: string;
  text?: string;
  role?: 'user' | 'assistant';
  id?: string;
  name?: string;
  args?: Record<string, unknown>;
  result?: unknown;
  state?: string;
  message?: string;
  code?: string;
  reason?: string;
}

// =============================================================================
// Hook Implementation
// =============================================================================

export function useGeminiLiveSession() {
  // State
  const [state, setState] = useState<VoiceSessionState>('idle');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
  const [toolCalls, setToolCalls] = useState<ToolCallInfo[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);

  // Refs
  const wsRef = useRef<WebSocket | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);

  // Audio playback queue
  const audioQueueRef = useRef<Float32Array[]>([]);
  const isPlayingRef = useRef(false);
  const nextStartTimeRef = useRef(0);

  // =============================================================================
  // Audio Playback
  // =============================================================================

  /**
   * Convert base64 to Float32Array for audio playback
   */
  const base64ToFloat32 = useCallback((base64: string): Float32Array => {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Convert Int16 to Float32
    const length = bytes.length / 2;
    const float32 = new Float32Array(length);
    for (let i = 0; i < length; i++) {
      let sample = bytes[i * 2] | (bytes[i * 2 + 1] << 8);
      if (sample >= 32768) sample -= 65536;
      float32[i] = sample / 32768;
    }
    return float32;
  }, []);

  /**
   * Play audio data from the queue
   */
  const playAudioQueue = useCallback(async () => {
    if (isPlayingRef.current) return;
    isPlayingRef.current = true;

    const ctx = audioContextRef.current;
    if (!ctx || ctx.state === 'closed') {
      isPlayingRef.current = false;
      return;
    }

    // Resume audio context if suspended
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }

    while (audioQueueRef.current.length > 0) {
      const audioData = audioQueueRef.current.shift()!;

      // Create audio buffer at 24kHz (Gemini output sample rate)
      const audioBuffer = ctx.createBuffer(1, audioData.length, 24000);
      audioBuffer.copyToChannel(audioData, 0);

      const source = ctx.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(ctx.destination);

      // Schedule playback
      if (nextStartTimeRef.current < ctx.currentTime) {
        nextStartTimeRef.current = ctx.currentTime;
      }
      source.start(nextStartTimeRef.current);
      nextStartTimeRef.current += audioBuffer.duration;
    }

    isPlayingRef.current = false;
  }, []);

  // =============================================================================
  // WebSocket Message Handler
  // =============================================================================

  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const message: ServerMessage = JSON.parse(event.data);

      switch (message.type) {
        case 'ready':
          setState('connected');
          setSessionId(message.sessionId || null);
          setError(null);
          break;

        case 'audio':
          if (message.data) {
            const audioData = base64ToFloat32(message.data);
            audioQueueRef.current.push(audioData);
            playAudioQueue();
          }
          break;

        case 'transcript':
          if (message.text) {
            setTranscript((prev) => [
              ...prev,
              {
                role: message.role || 'assistant',
                text: message.text!,
                timestamp: new Date(),
              },
            ]);
          }
          break;

        case 'toolCall':
          if (message.id && message.name) {
            setToolCalls((prev) => [
              ...prev,
              {
                id: message.id!,
                name: message.name!,
                args: message.args || {},
              },
            ]);
          }
          break;

        case 'toolResult':
          if (message.id) {
            setToolCalls((prev) =>
              prev.map((tc) =>
                tc.id === message.id ? { ...tc, result: message.result } : tc
              )
            );
          }
          break;

        case 'stateChange':
          if (message.state) {
            setState(message.state as VoiceSessionState);
          }
          break;

        case 'turnComplete':
          setState('connected');
          break;

        case 'error':
          setError(message.message || 'Unknown error');
          if (message.code === 'INIT_ERROR' || message.code === 'SESSION_ERROR') {
            setState('error');
          }
          break;

        case 'sessionEnd':
          setState('idle');
          setSessionId(null);
          break;
      }
    } catch (err) {
      console.error('[useGeminiLiveSession] Failed to parse message:', err);
    }
  }, [base64ToFloat32, playAudioQueue]);

  // =============================================================================
  // Connect / Disconnect
  // =============================================================================

  /**
   * Connect to the voice WebSocket server
   * Returns a promise that resolves when the session is ready
   */
  const connect = useCallback((config: VoiceSessionConfig = {}): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        resolve(); // Already connected
        return;
      }

      setState('connecting');
      setError(null);

      try {
        // Create audio context for playback
        audioContextRef.current = new AudioContext({ sampleRate: 24000 });

        // Determine WebSocket URL
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const host = config.wsUrl || `${protocol}//${window.location.host}`;
        const wsUrl = `${host}/api/voice/ws`;

        // Connect WebSocket
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        // Timeout for connection
        const timeout = setTimeout(() => {
          reject(new Error('Connection timeout'));
          ws.close();
        }, 10000);

        ws.onopen = () => {
          // Send config message
          ws.send(JSON.stringify({
            type: 'config',
            voice: config.voice || 'Kore',
          }));
        };

        // Wrap handleMessage to detect 'ready' state
        ws.onmessage = (event) => {
          handleMessage(event);
          // Check if this is the ready message
          try {
            const msg = JSON.parse(event.data);
            if (msg.type === 'ready') {
              clearTimeout(timeout);
              resolve();
            }
          } catch {
            // Ignore parse errors
          }
        };

        ws.onerror = (event) => {
          console.error('[useGeminiLiveSession] WebSocket error:', event);
          setError('Connection error');
          setState('error');
          clearTimeout(timeout);
          reject(new Error('Connection error'));
        };

        ws.onclose = (event) => {
          console.log('[useGeminiLiveSession] WebSocket closed:', event.reason);
          setState('idle');
          wsRef.current = null;
        };
      } catch (err) {
        console.error('[useGeminiLiveSession] Connect error:', err);
        setError(err instanceof Error ? err.message : 'Connection failed');
        setState('error');
        reject(err);
      }
    });
  }, [handleMessage]);

  /**
   * Disconnect from the voice session
   */
  const disconnect = useCallback(() => {
    // Stop recording
    if (isRecording) {
      stopRecording();
    }

    // Close WebSocket
    if (wsRef.current) {
      wsRef.current.send(JSON.stringify({ type: 'close' }));
      wsRef.current.close();
      wsRef.current = null;
    }

    // Close audio context
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    // Clear state
    setState('idle');
    setSessionId(null);
    audioQueueRef.current = [];
  }, [isRecording]);

  // =============================================================================
  // Audio Recording
  // =============================================================================

  /**
   * Start recording audio from microphone
   */
  const startRecording = useCallback(async () => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      throw new Error('Not connected to voice session');
    }

    if (isRecording) return;

    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      mediaStreamRef.current = stream;

      // Create audio context for capture at 16kHz
      const captureContext = new AudioContext({ sampleRate: 16000 });

      // Load and register the audio worklet
      await captureContext.audioWorklet.addModule('/audio-worklet.js');

      // Create source from microphone
      const source = captureContext.createMediaStreamSource(stream);
      sourceNodeRef.current = source;

      // Create worklet node
      const workletNode = new AudioWorkletNode(captureContext, 'audio-recorder-worklet');
      workletNodeRef.current = workletNode;

      // Handle audio data from worklet
      workletNode.port.onmessage = (event) => {
        if (event.data.type === 'audio' && event.data.int16arrayBuffer) {
          const int16Array = new Int16Array(event.data.int16arrayBuffer);

          // Convert to base64
          const bytes = new Uint8Array(int16Array.buffer);
          let binary = '';
          for (let i = 0; i < bytes.length; i++) {
            binary += String.fromCharCode(bytes[i]);
          }
          const base64 = btoa(binary);

          // Send to server
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({
              type: 'audio',
              data: base64,
            }));
          }
        }
      };

      // Connect nodes
      source.connect(workletNode);

      setIsRecording(true);
      setState('listening');
    } catch (err) {
      console.error('[useGeminiLiveSession] Recording error:', err);
      setError(err instanceof Error ? err.message : 'Failed to start recording');
      throw err;
    }
  }, [isRecording]);

  /**
   * Stop recording audio
   */
  const stopRecording = useCallback(() => {
    // Disconnect nodes
    if (sourceNodeRef.current) {
      sourceNodeRef.current.disconnect();
      sourceNodeRef.current = null;
    }
    if (workletNodeRef.current) {
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }

    // Stop media stream
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }

    // Notify server
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'audioEnd' }));
    }

    setIsRecording(false);
    setState('processing');
  }, []);

  /**
   * Send a text message (for testing or hybrid input)
   */
  const sendText = useCallback((text: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'text',
        content: text,
      }));

      // Add to transcript
      setTranscript((prev) => [
        ...prev,
        { role: 'user', text, timestamp: new Date() },
      ]);
    }
  }, []);

  /**
   * Clear the transcript history
   */
  const clearTranscript = useCallback(() => {
    setTranscript([]);
    setToolCalls([]);
  }, []);

  // =============================================================================
  // Cleanup on unmount
  // =============================================================================

  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  // =============================================================================
  // Return Hook API
  // =============================================================================

  return {
    // State
    state,
    sessionId,
    transcript,
    toolCalls,
    error,
    isRecording,

    // Actions
    connect,
    disconnect,
    startRecording,
    stopRecording,
    sendText,
    clearTranscript,
  };
}

export default useGeminiLiveSession;
